import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../env';

export default function ContactPage() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');

    try {
      const res = await fetch(`${API_URL}/v1/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message, website }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || t('contact.error.generic'));
      }

      setStatus('sent');
      setName('');
      setEmail('');
      setMessage('');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : t('contact.error.genericShort'));
    }
  }

  const inputClass =
    'w-full rounded-lg border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500';

  return (
    <div className="mx-auto max-w-2xl px-6 py-20 md:py-28">
      <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">{t('contact.heading')}</h1>
      <p className="mt-4 text-slate-400">
        {t('contact.description')}
      </p>

      {status === 'sent' ? (
        <div className="mt-10 rounded-2xl border border-slate-800/80 bg-slate-900/50 p-8 text-center">
          <div className="text-3xl text-emerald-400">&#10003;</div>
          <h2 className="mt-4 font-display text-lg font-semibold">{t('contact.success.heading')}</h2>
          <p className="mt-2 text-sm text-slate-400">
            {t('contact.success.description')}
          </p>

        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-10 space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-300">
              {t('contact.form.name')}
            </label>
            <input
              id="name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              placeholder={t('contact.form.namePlaceholder')}
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-300">
              {t('contact.form.email')}
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
              placeholder={t('contact.form.emailPlaceholder')}
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-slate-300">
              {t('contact.form.message')}
            </label>
            <textarea
              id="message"
              required
              minLength={10}
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className={inputClass}
              placeholder={t('contact.form.messagePlaceholder')}
            />
          </div>

          {/* Honeypot — hidden from real users */}
          <div className="absolute -left-[9999px]" aria-hidden="true">
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>

          {status === 'error' && (
            <p className="text-sm text-red-400">{errorMsg}</p>
          )}

          <button
            type="submit"
            disabled={status === 'sending'}
            className="rounded-lg bg-emerald-500 px-6 py-2.5 text-sm font-medium text-slate-950 hover:bg-emerald-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status === 'sending' ? t('contact.form.sending') : t('contact.form.submit')}
          </button>
        </form>
      )}
    </div>
  );
}
